import { useState, useEffect } from 'react';
import { apiUrl } from '../utils/api';

interface LocationData {
  country: string; state: string; city: string; address: string;
  coordinates?: { latitude: number; longitude: number };
}
interface ContactInfo { phone: string; email: string; }

export interface Campaign {
  id: string; userId: string; title: string; description: string;
  deadline: Date; location: LocationData; contactInfo: ContactInfo;
  privacyMode: 'private' | 'public'; publicLink: string;
  status: 'active' | 'expired' | 'completed' | 'cancelled';
  campaignType: 'solidarity' | 'family' | 'celebration';
  coAdministrators: string[]; isVerified: boolean;
  completionMessage?: string; completionDate?: Date; coverImageUrl?: string;
  createdAt: Date; updatedAt: Date;
}

export interface ProductWithAvailability {
  id: string; campaignId: string; name: string; totalNeeded: number;
  unit: string; description?: string; referenceLink?: string;
  createdAt: Date; updatedAt: Date;
  committedQuantity: number; availableQuantity: number;
  isFullyCommitted: boolean; commitmentPercentage: number;
}

export interface CampaignComment {
  id: string; campaignId: string; userId: string; parentCommentId?: string;
  content: string; isDonor: boolean; donorStatus?: 'verified' | 'delivered';
  user: { id: string; fullName: string };
  replies?: CampaignComment[]; createdAt: Date; updatedAt: Date;
}

export interface Commitment {
  id: string;
  donorName: string;
  donorEmail: string;
  donorPhone: string;
  items: { productId: string; productName: string; quantity: number; unit: string }[];
  status: 'pending' | 'verified' | 'delivered' | 'cancelled';
  isAnonymous: boolean;
  createdAt: string;
}

export interface OwnerProfile {
  id: string;
  fullName: string;
  profileImageUrl: string | null;
  title: string | null;
  bio: string | null;
}

export function useCampaignDetail(id: string | undefined) {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [products, setProducts] = useState<ProductWithAvailability[]>([]);
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [comments, setComments] = useState<CampaignComment[]>([]);
  const [ownerProfile, setOwnerProfile] = useState<OwnerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const userData = localStorage.getItem('user');
  const currentUser = userData ? JSON.parse(userData) : null;
  const isOwner = currentUser && campaign && currentUser.id === campaign.userId;

  const loadCampaignDetails = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const campaignResponse = await fetch(apiUrl(`/api/campaigns/${id}`), { headers });
      const campaignData = await campaignResponse.json();

      if (campaignData.success) {
        setCampaign(campaignData.data);

        // Load owner profile
        try {
          const profileRes = await fetch(apiUrl(`/api/profile/public/${campaignData.data.userId}`));
          const profileData = await profileRes.json();
          if (profileData.success) setOwnerProfile(profileData.data);
        } catch { /* ignore */ }

        const productsResponse = await fetch(apiUrl(`/api/products/campaign/${id}/availability`));
        const productsData = await productsResponse.json();
        if (productsData.success) setProducts(productsData.data.products || []);

        if (currentUser && currentUser.id === campaignData.data.userId) {
          const commitmentsResponse = await fetch(apiUrl(`/api/commitments/campaign/${id}`));
          const commitmentsData = await commitmentsResponse.json();
          if (commitmentsData.success) setCommitments(commitmentsData.data || []);
        }

        const commentsResponse = await fetch(apiUrl(`/api/comments/campaign/${id}`));
        const commentsData = await commentsResponse.json();
        if (commentsData.success) setComments(commentsData.data || []);
      } else {
        let errorMessage = 'Campaña no encontrada';
        if (campaignResponse.status === 404) {
          errorMessage = 'Campaña no encontrada o acceso denegado.';
        } else if (campaignResponse.status === 403) {
          errorMessage = 'No tienes permisos para ver esta campaña';
        }
        setError(errorMessage);
      }
    } catch {
      setError('Error al cargar la campaña. Verifica tu conexión a internet.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadCampaignDetails();
  }, [id]);

  return {
    campaign,
    products,
    commitments,
    setCommitments,
    comments,
    setComments,
    ownerProfile,
    isLoading,
    error,
    setError,
    currentUser,
    isOwner,
    reload: loadCampaignDetails,
  };
}
